import type { LazyImages } from "./images.ts";
import type { PreviewPlan } from "./plan.ts";

/**
 * Painting the Look panel's preview on a 2D canvas (ADR-094 decision 27): the planned sheets' standing cell copied
 * from each sheet, bottom first, one canvas pixel to one pixel of art. The canvas is the stage's size and is
 * magnified by its CSS size, a whole number of logical pixels to one, with no smoothing. A look is painted whole or
 * not at all, as the world composes a block: while any sheet of the plan has not arrived the canvas is left as it
 * was, so the look before stays until the new one is whole and a character is never shown without its clothes, its
 * hair or its body for the time a sheet takes to load.
 */

/**
 * Paints one frame.
 *
 * @param context The canvas's context; the canvas is the plan's stage in size.
 * @param plan What to draw.
 * @param images The sheet images.
 * @returns How many of the planned sheets were drawn: every one of them once all have arrived, and none, with the
 *   canvas untouched, while any is still arriving. Every sheet of the plan is asked for either way.
 */
export function paintPreview(context: CanvasRenderingContext2D, plan: PreviewPlan, images: LazyImages): number {
  const { cell, stage } = plan;
  const arrived: HTMLImageElement[] = [];
  for (const sheet of plan.sheets) {
    const image = images.get(sheet.path);
    if (image !== null) {
      arrived.push(image);
    }
  }
  if (arrived.length < plan.sheets.length) {
    return 0;
  }
  context.imageSmoothingEnabled = false;
  context.clearRect(0, 0, stage.width, stage.height);
  for (const image of arrived) {
    context.drawImage(image, cell.x, cell.y, cell.width, cell.height, cell.offsetX - stage.left, cell.offsetY - stage.top, cell.width, cell.height);
  }
  return arrived.length;
}
